import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';

/**
 * ATAJOS DE TECLADO.
 *
 * Maciel pasa el día en esta pantalla. Cuando el trabajo es repetitivo —
 * registrar venta tras venta— la mano en el teclado gana mucho al viaje al
 * sidebar.
 *
 * ═══ LA REGLA QUE EVITA EL DESASTRE ═══
 *
 * Ningún atajo dispara si se está escribiendo. Sin esa guarda, teclear
 * «Nuevo vestido» en un campo abriría formularios y navegaría a media palabra.
 * Se comprueba el elemento con foco: `input`, `textarea`, `select` o cualquier
 * cosa editable quedan fuera.
 *
 * ═══ `G` + LETRA ═══
 *
 * `G V` va a Ventas, `G F` a Finanzas, `G I` a Inventario, `G P` a Productos.
 * La `G` abre una ventana de un segundo y medio; si no llega la segunda tecla,
 * se cancela sola. Es el patrón de Gmail y de GitHub, ya está en los dedos de
 * mucha gente.
 */

const DESTINOS: Record<string, { ruta: string; nombre: string }> = {
  v: { ruta: '/ventas', nombre: 'Ventas' },
  f: { ruta: '/finanzas', nombre: 'Finanzas' },
  i: { ruta: '/inventario', nombre: 'Inventario' },
  p: { ruta: '/productos', nombre: 'Productos' },
};

function escribiendo(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const etiqueta = el.tagName.toLowerCase();
  return (
    etiqueta === 'input' ||
    etiqueta === 'textarea' ||
    etiqueta === 'select' ||
    el.isContentEditable
  );
}

export interface AtajosProps {
  /** `N`: crear algo en el módulo actual. */
  onNuevo?: () => void;
  children?: ReactNode;
}

export function Atajos({ onNuevo }: AtajosProps) {
  const navegar = useNavigate();
  const [esperandoIr, setEsperandoIr] = useState(false);
  const [panel, setPanel] = useState(false);

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // `?` abre la ayuda incluso con Shift, pero nunca mientras se escribe.
      if (e.key === '?' && !escribiendo()) {
        e.preventDefault();
        setPanel((v) => !v);
        return;
      }
      if (e.key === 'Escape') {
        setPanel(false);
        setEsperandoIr(false);
        return;
      }
      if (escribiendo()) return;

      const tecla = e.key.toLowerCase();

      if (esperandoIr) {
        const destino = DESTINOS[tecla];
        setEsperandoIr(false);
        if (destino) {
          e.preventDefault();
          navegar(destino.ruta);
        }
        return;
      }

      if (tecla === 'g') {
        e.preventDefault();
        setEsperandoIr(true);
        // Si la segunda tecla no llega, la `G` no se queda armada: una `G`
        // colgada haría que la siguiente letra navegara sin motivo.
        setTimeout(() => setEsperandoIr(false), 1500);
        return;
      }

      if (tecla === 'n' && onNuevo) {
        e.preventDefault();
        onNuevo();
        return;
      }

      if (tecla === '/') {
        const buscador = document.querySelector<HTMLInputElement>('[data-tabla-buscador]');
        if (buscador) {
          e.preventDefault();
          buscador.focus();
          buscador.select();
        }
      }
    };

    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [esperandoIr, navegar, onNuevo]);

  return (
    <>
      {esperandoIr && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-sm text-white shadow-lg">
          Ir a… <kbd className="mx-0.5 rounded bg-white/20 px-1">V</kbd> Ventas
          <kbd className="mx-0.5 ml-2 rounded bg-white/20 px-1">F</kbd> Finanzas
          <kbd className="mx-0.5 ml-2 rounded bg-white/20 px-1">I</kbd> Inventario
          <kbd className="mx-0.5 ml-2 rounded bg-white/20 px-1">P</kbd> Productos
        </div>
      )}

      {panel && <PanelDeAtajos onCerrar={() => setPanel(false)} />}
    </>
  );
}

const LISTA: Array<{ teclas: string[]; que: string }> = [
  { teclas: ['⌘', 'K'], que: 'Buscar o hacer algo' },
  { teclas: ['N'], que: 'Crear algo nuevo en esta pantalla' },
  { teclas: ['/'], que: 'Ir al buscador de la tabla' },
  { teclas: ['G', 'V'], que: 'Ir a Ventas' },
  { teclas: ['G', 'F'], que: 'Ir a Finanzas' },
  { teclas: ['G', 'I'], que: 'Ir a Inventario' },
  { teclas: ['G', 'P'], que: 'Ir a Productos' },
  { teclas: ['Esc'], que: 'Cerrar el panel de detalle' },
  { teclas: ['?'], que: 'Ver esta lista' },
];

function PanelDeAtajos({ onCerrar }: { onCerrar: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onCerrar}
        className="absolute inset-0 bg-slate-900/40"
      />
      <div className="relative w-full max-w-md rounded-xl border border-surface-border bg-surface p-5 shadow-2xl">
        <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
          Atajos de teclado
        </h2>
        <ul className="space-y-1.5">
          {LISTA.map((a) => (
            <li key={a.que} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-slate-600 dark:text-slate-300">{a.que}</span>
              <span className="flex shrink-0 gap-1">
                {a.teclas.map((t) => (
                  <kbd
                    key={t}
                    className={cn(
                      'rounded border border-surface-border bg-slate-50 px-1.5 py-0.5',
                      'font-mono text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300',
                    )}
                  >
                    {t}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-slate-500">
          Ninguno funciona mientras estás escribiendo en un campo.
        </p>
      </div>
    </div>
  );
}
